import React, { useContext, useEffect, useRef } from 'react';
import { styled } from '@mui/material';

import { SystemAvatar } from 'components/organisms/Milestones';
import { PageContext } from 'pages/TaskManager/PageProvider';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    RichTextArea,
    RubricTable,
    Stack,
    Text,
} from 'shell/components';
import { getHTMLWordCount } from 'utils/helper';

const TabletFormativeSetup = ({
    steps,
    formative,
    isSubmitLoading,
    isSaveLoading,
    isPreviewLoading,
    currentStepIndex,
    hasChanges,
    preview,
    previewData,
    isPaused,
    togglePause,
    handlers,
    onClose,
    onClickNext,
    onClickBack,
    onClickSave,
    onClickComplete,
    onClickGeneratePreview,
    onClickEditContext,
}) => {
    const { formativeForm, formativeErrors } = useContext(PageContext);

    const contentContainerRef = useRef(null);
    const previewBottomRef = useRef(null);

    const scrollToBottom = () => {
        if (previewBottomRef.current) previewBottomRef.current.scrollIntoView();
    };

    const {
        // step 'context'
        handleRubricSort,
        handleChangeRubric,
        handleClickAddCriterion,
        handleClickEditCriterion,
        handleClickAddOption,
        handleClickDeleteOption,
        handleDragEnd,
        handleChangeAssignmentDescription,
        handleChangeAdditionalSpecifications,
        // step 'baseline'
        handleChangePastAssignment,
        handleChangeGrading,
        // step 'preview'
        handleChangeExampleAssignment,
    } = handlers;

    const renderDialogContent = () => {
        const Content = {
            context: (
                <Stack spacing={5}>
                    <Stack spacing={3}>
                        <Text variant={'h6'} weight={'semibold'}>
                            Provide Context
                        </Text>
                        <Text>
                            The feedback provided by the AI system will be guided by the patterns observed in your
                            existing assessment practices. Although students won’t receive grades, it is essential for
                            you to supply a project rubric to ensure that our AI can offer precise feedback and
                            suggestions within the appropriate context.
                        </Text>
                    </Stack>
                    <Stack spacing={2}>
                        <Text>
                            1. Create a Rubric{' '}
                            <Text component={'span'} color={'error'}>
                                *
                            </Text>
                        </Text>
                        <RubricTable
                            type={'create'}
                            data={formativeForm.rubric}
                            errors={formativeErrors.rubric}
                            sort={formativeForm.rubricSort}
                            setSort={handleRubricSort}
                            onChange={handleChangeRubric}
                            onClickAddCriterion={handleClickAddCriterion}
                            onClickEditCriterion={handleClickEditCriterion}
                            onClickAddOption={handleClickAddOption}
                            onClickDeleteOption={handleClickDeleteOption}
                            onDragEnd={handleDragEnd}
                        />
                    </Stack>
                    <RichTextArea
                        error={Boolean(formativeErrors.assignmentDescription)}
                        helperText={`${getHTMLWordCount(formativeForm.assignmentDescription)}/500 words`}
                        helperTextPosition={'end'}
                        label={'2. Assignment Description'}
                        labelId={'assignment-description-input'}
                        placeholder={
                            'Providing an assignment description helps the AI offer more precise and tailored feedback to students, aligning with the assignment’s objectives and requirements.'
                        }
                        required
                        value={formativeForm.assignmentDescription}
                        onChange={(html) => {
                            handleChangeAssignmentDescription(html);
                        }}
                    />
                    <RichTextArea
                        error={Boolean(formativeErrors.additionalSpecifications)}
                        helperText={`${getHTMLWordCount(formativeForm.additionalSpecifications)}/250 words`}
                        helperTextPosition={'end'}
                        label={'3. Additional Specifications'}
                        labelId={'additional-specifications-input'}
                        placeholder={
                            'Provide any additional specifications or specific areas of focus for the AI’s feedback. For example, you may request the AI to pay special attention to critical analysis skills or provide guidance on incorporating scholarly sources effectively.'
                        }
                        value={formativeForm.additionalSpecifications}
                        onChange={handleChangeAdditionalSpecifications}
                    />
                </Stack>
            ),
            baseline: (
                <Stack sx={{ mb: -5 }} spacing={5}>
                    <Stack spacing={7}>
                        <Stack spacing={3}>
                            <Text variant={'h6'} weight={'semibold'}>
                                Establish a Baseline
                            </Text>
                            <Text>
                                Please provide an example of a past assignment with the grades and feedback it received.
                                This helps the AI to establish a baseline on the rubric based on your assessment
                                practices.
                            </Text>
                        </Stack>
                        <RichTextArea
                            key={'past-assignment-input'}
                            sx={{ '& > .MuiRichTextArea-root': { height: '20rem' } }}
                            error={Boolean(formativeErrors.pastAssignment)}
                            helperText={`${getHTMLWordCount(formativeForm.pastAssignment)}/2500 words`}
                            helperTextPosition={'end'}
                            label={'Past Assignment'}
                            labelId={'past-assignment-input'}
                            placeholder={'Please paste the assignment here'}
                            value={formativeForm.pastAssignment}
                            onChange={handleChangePastAssignment}
                        />
                    </Stack>
                    <BackgroundStack>
                        <RubricTable
                            sx={{
                                '& > div:first-child': {
                                    display: 'none',
                                },
                                '& > div:nth-child(2)': {
                                    borderTop: 'none',
                                    borderRadius: '12px 12px 0 0',
                                },
                            }}
                            type={'submit'}
                            data={formativeForm.rubric}
                            errors={formativeErrors.grading}
                            form={formativeForm.grading}
                            name={''}
                            number={'1'}
                            total={'1'}
                            onChange={handleChangeGrading}
                        />
                    </BackgroundStack>
                </Stack>
            ),
            preview: (
                <Stack sx={{ mb: -5 }} spacing={5}>
                    <Stack spacing={7}>
                        <Stack spacing={3}>
                            <Text variant={'h6'} weight={'semibold'}>
                                Student Preview
                            </Text>
                            <Text>
                                Add an assignment here to see what feedback to students might look like. If everything
                                looks good, you can complete the setup process!
                            </Text>
                        </Stack>
                        <RichTextArea
                            key={'example-assignment-input'}
                            sx={{ '& > .MuiRichTextArea-root': { height: '20rem' } }}
                            helperText={`${getHTMLWordCount(formativeForm.exampleAssignment)}/2500 words`}
                            helperTextPosition={'end'}
                            label={'Example Assignment'}
                            labelId={'example-assignment-input'}
                            placeholder={'Please paste the assignment here'}
                            value={formativeForm.exampleAssignment}
                            onChange={handleChangeExampleAssignment}
                        />
                    </Stack>
                    <BackgroundStack spacing={2}>
                        <Text>AI Generated Feedback</Text>
                        <PreviewRootStack>
                            {preview ? (
                                <PreviewTextStack direction={'row'} spacing={2}>
                                    <SystemAvatar />
                                    <Stack spacing={1}>
                                        <Text color={'light'}>Ensightful AI</Text>
                                        <Text sx={{ pb: 5 }} rich>
                                            {preview}
                                        </Text>
                                        <Box ref={previewBottomRef} />
                                    </Stack>
                                </PreviewTextStack>
                            ) : (
                                <CenteredButton
                                    disabled={!Boolean(formativeForm.exampleAssignment)}
                                    loading={isPreviewLoading}
                                    variant={'secondary'}
                                    onClick={onClickGeneratePreview}
                                >
                                    Generate Preview
                                </CenteredButton>
                            )}
                        </PreviewRootStack>
                        <Stack
                            sx={{ pt: 3 }}
                            alignItems={'center'}
                            direction={'row'}
                            justifyContent={'space-between'}
                            spacing={3}
                        >
                            <Text align={'left'} color={'light'}>
                                Not looking quite right? Once the preview has finished generating you can edit the
                                context by adding specifications about tone, content and more!
                            </Text>
                            {!isPreviewLoading && previewData !== null && preview === previewData ? (
                                <Button sx={{ flex: 'none' }} variant={'tertiary'} onClick={onClickEditContext}>
                                    Edit Context
                                </Button>
                            ) : (
                                <Button
                                    sx={{ flex: 'none' }}
                                    disabled={preview === null || previewData === null}
                                    variant={'tertiary'}
                                    onClick={togglePause}
                                >
                                    {isPaused ? 'Resume Generation' : 'Pause Generation'}
                                </Button>
                            )}
                        </Stack>
                    </BackgroundStack>
                </Stack>
            ),
        }[steps[currentStepIndex].key];

        return <CustomDialogContent ref={contentContainerRef}>{Content}</CustomDialogContent>;
    };

    useEffect(() => {
        if (contentContainerRef.current) contentContainerRef.current.scrollTop = 0;
    }, [currentStepIndex]);

    useEffect(() => {
        scrollToBottom();
    }, [preview]);

    return (
        <>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} spacing={4} justifyContent={'space-between'}>
                    <Stack
                        sx={{ flex: 1 }}
                        alignItems={'center'}
                        direction={'row'}
                        spacing={2}
                        justifyContent={'space-between'}
                    >
                        {steps.map((step, index) => (
                            <CustomStack key={step.key}>
                                <Text color={currentStepIndex >= index ? 'primary' : 'light'}>{step.value}</Text>
                                <IndicatorBox active={currentStepIndex >= index} />
                            </CustomStack>
                        ))}
                    </Stack>
                    <IconButton
                        aria-label={'close'}
                        disabled={isSubmitLoading || isSaveLoading || isPreviewLoading || preview !== previewData}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            {renderDialogContent()}
            <Divider />
            <DialogActions>
                <Stack sx={{ width: '100%' }} direction={'row-reverse'} justifyContent={'space-between'}>
                    <Stack direction={'row'} spacing={4}>
                        <Button
                            disabled={!hasChanges || isSubmitLoading}
                            loading={isSaveLoading}
                            variant={'secondary'}
                            onClick={onClickSave}
                        >
                            Save
                        </Button>
                        {currentStepIndex === steps.length - 1 ? (
                            <Button
                                disabled={isSaveLoading || isPreviewLoading || preview !== previewData}
                                loading={isSubmitLoading}
                                onClick={formative?.status !== 'complete' ? onClickComplete : onClose}
                            >
                                {formative?.status !== 'complete' ? 'Complete Setup' : 'Close'}
                            </Button>
                        ) : (
                            <Button onClick={onClickNext}>Next</Button>
                        )}
                    </Stack>
                    {currentStepIndex !== 0 && (
                        <Button
                            color={'neutral'}
                            disabled={isPreviewLoading || preview !== previewData}
                            variant={'tertiary'}
                            onClick={onClickBack}
                        >
                            Back
                        </Button>
                    )}
                </Stack>
            </DialogActions>
        </>
    );
};

const CustomStack = styled(Stack)(() => {
    return {
        flex: 1,
    };
});

const IndicatorBox = styled(Box)(({ active, theme }) => {
    return {
        borderRadius: '4px',
        width: '100%',
        height: '8px',
        background: active ? theme.palette.ensightful.primary[500] : theme.palette.ensightful.neutral[400],
    };
});

const CustomDialogContent = styled(DialogContent)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

const BackgroundStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '12px 12px 0 0',
        margin: theme.spacing(0, -6),
        padding: theme.spacing(5),
        background: theme.palette.ensightful.shades[0],
        boxShadow: theme.palette.ensightful.shadows[1],
    };
});

const PreviewRootStack = styled(Stack)(({ theme }) => {
    return {
        position: 'relative',
        borderRadius: '12px',
        height: '550px',
        background: theme.palette.ensightful.neutral[50],
    };
});

const PreviewTextStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(5),
        overflowY: 'auto',
    };
});

const CenteredButton = styled(Button)(() => {
    return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    };
});

export default TabletFormativeSetup;
